"use client";

import { PropsWithChildren, useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { AppShell } from "@/components/app-shell";
import { LoadingScreen } from "@/components/loading-screen";
import { ToastHost } from "@/components/ui/toast-host";
import { ConfirmHost } from "@/components/ui/confirm";
import { useAuth } from "@/components/auth-provider";
import { useTheme } from "@/components/theme-provider";

export function PortalFrame({ children }: PropsWithChildren) {
  const pathname = usePathname();
  const router = useRouter();
  const { user, isLoading } = useAuth();
  const theme = useTheme();

  useEffect(() => {
    if (!isLoading && !user && pathname !== "/login") {
      router.replace("/login");
      return;
    }

    // Авторизованного уводим с /login на портал — КРОМЕ пейнов /theme-compare
    // (портал в iframe): там показываем кандидаты входной страницы (и слева
    // текущий вход, и справа кандидат под превью-флагом). Реальный портал
    // (top-level) ведёт себя как раньше.
    let framed = false;
    try { framed = window.self !== window.top; } catch { framed = true; }
    if (!isLoading && user && pathname === "/login" && !framed) {
      router.replace("/");
    }
  }, [isLoading, pathname, router, user]);

  if (isLoading || theme.isLoading) {
    return <LoadingScreen label="Загружаем платформу..." />;
  }

  if (pathname === "/login") {
    return <>{children}<ToastHost /><ConfirmHost /></>;
  }

  if (!user) {
    return <LoadingScreen label="Перенаправляем на страницу входа..." />;
  }

  // Инструмент сравнения тем — полноэкранный, без AppShell (но за авторизацией).
  if (pathname === "/theme-compare") {
    return <>{children}<ToastHost /><ConfirmHost /></>;
  }

  return (
    <>
      <AppShell>{children}</AppShell>
      {/* Один хост тостов на весь портал: ошибки сохранения показываются
          отсюда, из любого экрана (см. lib/toast.ts). */}
      <ToastHost />
      {/* Один хост подтверждений — confirmDialog() вместо window.confirm()
          (см. components/ui/confirm.tsx). */}
      <ConfirmHost />
    </>
  );
}
